import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { Image } from '../design-system';
import { RarityBadge } from '../design-system/RarityBadge';
import type { JustPulledItem } from '../api';
import { isRarePlus } from '../reveal';

export interface JustPulledChipProps {
  item: JustPulledItem;
  /** Compact mobile variant: smaller thumbnail and tighter gap. */
  compact?: boolean;
  /** When provided, the chip becomes interactive and opens the card modal. */
  onClick?: (item: JustPulledItem) => void;
}

const CARD_BG =
  'linear-gradient(180deg, #C2C3C5 -16.4%, #F7F8F8 121.88%), #FFF';

// Chip background flows through the Design Constructor (`--co-JustPulled-chipBg*`)
// with separate knobs for common and rare chips, mirroring the border knobs
// below. Fallbacks reproduce the original hardcoded gradient for both.
const COMMON_CHIP_BG =
  'linear-gradient(180deg, var(--co-JustPulled-chipBgFrom, rgba(255,255,255,0.20)) 0%, var(--co-JustPulled-chipBgTo, rgba(153,153,153,0.20)) 100%)';
const RARE_CHIP_BG =
  'linear-gradient(180deg, var(--co-JustPulled-chipRareBgFrom, rgba(255,255,255,0.20)) 0%, var(--co-JustPulled-chipRareBgTo, rgba(153,153,153,0.20)) 100%)';

// Chip border flows through the JustPulled Design Constructor surface, with
// separate knobs for rare and common chips. Defaults reproduce the original
// look: common chips borderless (width 0), rare chips a 1px white highlight.
const COMMON_CHIP_BORDER: CSSProperties = {
  borderStyle: 'solid',
  borderWidth: 'var(--co-JustPulled-chipBorderWidth, 0px)',
  borderColor: 'var(--co-JustPulled-chipBorderColor, #ffffff)',
};
const RARE_CHIP_BORDER: CSSProperties = {
  borderStyle: 'solid',
  borderWidth: 'var(--co-JustPulled-chipRareBorderWidth, 1px)',
  borderColor: 'var(--co-JustPulled-chipRareBorderColor, #ffffff)',
};

/**
 * A single pulled-card chip. Rare+ badges get a white border + gold tier
 * tag; Common / Uncommon stay plain (the backend sends a badge for every
 * tier, the highlight gate lives here).
 */
export function JustPulledChip({ item, compact = false, onClick }: JustPulledChipProps) {
  const isRare = isRarePlus(item.badge?.name);
  // Match Figma sizes: 24px thumb on mobile (frame 226:2001), 36px on
  // desktop (frame 167:9668). 5px chip padding above/below means total
  // chip height stays inside the 34px-tall mobile marquee row.
  const thumbSize = compact ? 24 : 36;

  const interactive = typeof onClick === 'function';
  const handleClick = (e: MouseEvent) => {
    if (!interactive) return;
    // Don't bubble to the parent section's tap-to-open list handler —
    // chip click opens the modal, the surrounding strip opens the page.
    e.stopPropagation();
    onClick!(item);
  };
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClick!(item);
    }
  };

  return (
    <article
      className={
        'flex shrink-0 items-center [border-radius:var(--co-JustPulled-chipBorderRadius,12px)] ' +
        'shadow-[0_1px_2px_0_rgba(25,25,25,0.10)] ' +
        (compact ? 'gap-[6px] p-[5px]' : 'gap-[8px] p-[5px] pr-[10px]') +
        (interactive ? ' cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]' : '')
      }
      style={{
        background: isRare ? RARE_CHIP_BG : COMMON_CHIP_BG,
        ...(isRare ? RARE_CHIP_BORDER : COMMON_CHIP_BORDER),
      }}
      aria-label={item.title || 'Just pulled item'}
      {...(interactive
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: handleClick,
            onKeyDown: handleKeyDown,
          }
        : {})}
    >
      <div
        className="shrink-0 overflow-hidden rounded-[6px]"
        style={{
          width: thumbSize,
          height: thumbSize,
          background: CARD_BG,
        }}
      >
        <Image
          src={item.imageUrl}
          alt=""
          className="h-full w-full"
          imgClassName="absolute inset-0 h-full w-full object-cover"
          width={thumbSize}
          height={thumbSize}
          quality={90}
        />
      </div>

      <p
        className="type-subhead truncate text-white"
        style={{
          maxWidth: compact ? 180 : 240,
        }}
      >
        {item.title}
      </p>

      {isRare && item.badge?.name && (
        <RarityBadge variant="text">{item.badge.name}</RarityBadge>
      )}
    </article>
  );
}
